import { guessTwoSpeakerFaceMap } from '@src/services/framing/guessTwoSpeakerFaceMap';
import { loadSpeakerLabelToFaceLabels } from '@src/services/framing/loadSpeakerLabelToFaceLabels';
import { AspectRatio } from '@src/types.ts/video';
import { LogManager } from '@src/utility/Logger';
import parseAspectRatio from '@src/utility/parseAspectRatio';

import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { NaturalDimensions } from '@src/editor/types/aspectRatio';
import type { SceneDetection } from '@src/editor/types/sceneDetection';
import type { FaceDetection } from '@src/hooks/useFaceDetections';
import type { Video } from '@src/types.ts/story';

const logger = LogManager.getLogger('store:AutoFramingHelper');

/**
 * Strategy used by `autoFrameNewAspectRatio`. Driven by the
 * `autoFramingStrategy` LaunchDarkly flag.
 *
 * - `transcript-active-speaker` (default) — replaces the scenes with one per
 *   speaker turn, also split at camera cuts (`autoFrameScenesPerSpeaker`), and
 *   frames each on its speaker. Falls back to framing the existing scenes by
 *   speaker when the transcript yields no turns. Speaker scenes need only the
 *   transcript, so they are made even without speaker→face mappings (the
 *   helper builds them itself when callers don't supply them); a two-speaker
 *   video then frames on a guessed map, anything else face-only.
 * - `basic` — `autoFrameAllScenes`, face-only framing on the existing scenes.
 *   Kill-switch for the speaker path.
 */
export type AutoFramingStrategy = 'basic' | 'transcript-active-speaker';

/**
 * Resolves the raw flag value: `basic` is the explicit kill-switch; anything
 * else (including unset) is `transcript-active-speaker`.
 */
export function resolveAutoFramingStrategy(
  value: string | undefined | null,
): AutoFramingStrategy {
  return value === 'basic' ? 'basic' : 'transcript-active-speaker';
}

export interface AutoFramingContext {
  /** The video editor instance */
  editor: IVideoEditor | undefined;
  /** Current layout configuration from the editor store */
  editorLayoutConfig: {
    strategy: string;
    sceneCountThreshold: number;
  };
  /** Preloaded face detections */
  faceDetections: FaceDetection[] | null;
  /** Scene manipulation limit from feature flags */
  sceneManipulationLimit: number;
  /** Function to preload face detections if not available */
  preloadFaceDetections: (storyId: string) => Promise<void>;
  /** Current story ID */
  storyId: string | undefined;
  /**
   * Auto-framing strategy from LaunchDarkly, see `resolveAutoFramingStrategy`.
   */
  autoFramingStrategy?: AutoFramingStrategy | string;
  /** Shot detections in media time — speaker scenes are also split at each cut. */
  shots?: ReadonlyArray<SceneDetection> | null;
  /** Keyed by `VideoElement.source`; gives each speaker scene a cover crop. */
  naturalMediaDimensions?: Record<string, NaturalDimensions>;
  /**
   * Optional pre-built speaker_label → face_label map. When omitted and the
   * strategy is `transcript-active-speaker`, the helper builds it itself
   * from `storyId` via `loadSpeakerLabelToFaceLabels`.
   */
  speakerLabelToFaceLabels?: Record<string, string[]> | null;
}

/**
 * Auto-frames all scenes for a new aspect ratio using face detection.
 * Only applies to 9:16 and 1:1 aspect ratios.
 * @param newAspectRatio - The target aspect ratio
 * @param context - The auto-framing context with dependencies
 * @returns Promise that resolves when auto-framing is complete
 */
export async function autoFrameNewAspectRatio(
  newAspectRatio: Video['aspectRatio'],
  context: AutoFramingContext,
): Promise<void> {
  const {
    editor,
    editorLayoutConfig,
    faceDetections,
    sceneManipulationLimit,
    preloadFaceDetections,
    storyId,
  } = context;

  // Only auto-frame for 9:16 and 1:1 aspect ratios
  if (
    newAspectRatio !== AspectRatio.AR_9_16 &&
    newAspectRatio !== AspectRatio.AR_1_1
  ) {
    logger.debug('Skipping auto-frame: not 9:16 or 1:1 aspect ratio', {
      newAspectRatio,
    });
    return;
  }

  if (!editor) {
    logger.warn('Skipping auto-frame: editor not available');
    return;
  }

  // Check scene count before proceeding
  const sceneCount = editor.timeline.scenes?.length || 0;

  if (
    sceneManipulationLimit > 0 &&
    sceneCount > sceneManipulationLimit &&
    (editorLayoutConfig.strategy === 'split' ||
      editorLayoutConfig.strategy === 'hybrid')
  ) {
    logger.info('Skipping auto-frame: scene count exceeds limit', {
      sceneCount,
      sceneManipulationLimit,
    });
    return;
  }

  // Load face detections if not already loaded
  let currentFaceDetections = faceDetections;
  if (!currentFaceDetections || currentFaceDetections.length === 0) {
    logger.info('Face detections empty, attempting to load');
    if (storyId) {
      await preloadFaceDetections(storyId);
      // Note: faceDetections is passed by reference, so we rely on the store being updated
    }
  }

  // Skip if still no face detections
  if (!currentFaceDetections || currentFaceDetections.length === 0) {
    logger.info('Skipping auto-frame: no face detections available');
    return;
  }

  // Create SmartFramingService and auto-frame all scenes
  const targetAspectRatio =
    newAspectRatio !== AspectRatio.AR_9_16
      ? (parseAspectRatio(newAspectRatio) ?? undefined)
      : undefined;

  try {
    // Use dynamic imports to avoid circular dependency
    const { SmartFramingService } =
      await import('@src/services/framing/SmartFramingService');
    const { SceneService } = await import('@src/services/SceneService');
    const { LayoutEngine } =
      await import('@src/editor/models/layout/LayoutEngine');

    const framingService = new SmartFramingService(
      editor,
      new SceneService(),
      new LayoutEngine(),
    );

    const strategy = resolveAutoFramingStrategy(context.autoFramingStrategy);

    // When active-speaker is desired and the caller didn't supply a pre-built
    // map, resolve it lazily from the story.
    let speakerLabelToFaceLabels = context.speakerLabelToFaceLabels ?? null;
    if (
      strategy !== 'basic' &&
      (!speakerLabelToFaceLabels ||
        Object.keys(speakerLabelToFaceLabels).length === 0) &&
      storyId
    ) {
      const loaded = await loadSpeakerLabelToFaceLabels(storyId);
      speakerLabelToFaceLabels = loaded.ok ? loaded.value : null;
    }
    let speakerMap =
      speakerLabelToFaceLabels &&
      Object.keys(speakerLabelToFaceLabels).length > 0
        ? speakerLabelToFaceLabels
        : null;
    // No Face Mapping: a two-person video still alternates, on a guess the
    // reframe modal can correct in one step (PLAT-11992).
    if (!speakerMap && strategy !== 'basic') {
      const guessed = guessTwoSpeakerFaceMap(
        editor.getOriginalTranscript() ?? [],
        currentFaceDetections,
      );
      speakerMap = guessed.ok ? guessed.value : null;
    }

    let framedCount: number | null = null;
    let resolvedStrategy: AutoFramingStrategy = strategy;
    let speakerScenesCreated = false;

    // Speaker turns need only the transcript, so split by speaker even with
    // no mapping; those scenes frame face-only until faces are mapped.
    if (strategy === 'transcript-active-speaker') {
      const { autoFrameScenesPerSpeaker } =
        await import('@src/services/framing/autoFrameScenesPerSpeaker');
      const result = autoFrameScenesPerSpeaker({
        editor,
        faceDetections: currentFaceDetections,
        speakerLabelToFaceLabels: speakerMap ?? {},
        shots: context.shots,
        naturalMediaDimensions: context.naturalMediaDimensions,
        targetAspectRatio,
      });
      if (result.ok) {
        framedCount = result.value.framedCount;
        speakerScenesCreated = true;
      } else {
        logger.info(
          'Could not create speaker scenes; framing the existing scenes by speaker instead',
          { code: result.error.code, message: result.error.message },
        );
      }
    }

    if (framedCount === null) {
      if (strategy === 'transcript-active-speaker' && speakerMap) {
        framedCount = framingService.autoFrameAllScenesWithSpeakerContext(
          currentFaceDetections,
          speakerMap,
          targetAspectRatio,
        );
      } else {
        if (strategy !== 'basic') {
          logger.info(
            `autoFramingStrategy=${strategy} requested but speaker→face mappings are unavailable; falling back to basic`,
          );
        }
        resolvedStrategy = 'basic';
        framedCount = framingService.autoFrameAllScenes(
          currentFaceDetections,
          targetAspectRatio,
        );
      }
    }

    logger.info('Auto-framed new aspect ratio', {
      newAspectRatio,
      sceneCount: editor.timeline.scenes.length,
      framedCount,
      requestedStrategy: strategy,
      resolvedStrategy,
      speakerScenesCreated,
      hasSpeakerMapping: !!speakerMap,
    });
  } catch (error) {
    logger.error('Failed to auto-frame new aspect ratio', {
      newAspectRatio,
      error,
    });
  }
}
